import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Send,
  Download,
  FileText,
  BookOpen,
  Check,
  Copy,
  Loader2,
  Trash2,
  Settings2,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { api, formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { renderMarkdown } from "@/lib/markdown";

const STAGES = [
  { id: 1, name: "Exploración inicial", description: "Precisar tema y problemática" },
  { id: 2, name: "Delimitación del problema", description: "Delimitar el estudio" },
  { id: 3, name: "Pregunta y objetivos", description: "Formular pregunta y objetivos" },
  { id: 4, name: "Justificación", description: "Argumentar relevancia" },
  { id: 5, name: "Marco teórico", description: "Conceptos y antecedentes" },
  { id: 6, name: "Diseño metodológico", description: "Enfoque y técnicas" },
  { id: 7, name: "Instrumentos", description: "Diseño de instrumentos" },
  { id: 8, name: "Análisis de datos", description: "Organización y análisis" },
  { id: 9, name: "Redacción del informe", description: "Estructura formal" },
  { id: 10, name: "Revisión final", description: "Coherencia y calidad" },
];

export default function ProjectEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const [project, setProject] = useState(null);
  const [messages, setMessages] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showMeta, setShowMeta] = useState(false);
  const [metaForm, setMetaForm] = useState(null);
  const [showTemplateId, setShowTemplateId] = useState(null);

  const chatEndRef = useRef(null);
  const pollTimerRef = useRef(null);

  // Initial fetch
  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const [p, m, t] = await Promise.all([
          api.get(`/projects/${id}`),
          api.get(`/chat/messages/${id}`),
          api.get(`/templates`),
        ]);
        if (!mounted) return;
        setProject(p.data);
        setMessages(m.data);
        setTemplates(t.data);
        // If there's a pending message from a previous session, resume polling
        if (m.data.some((msg) => msg.status === "pending")) {
          startPolling();
        }
      } catch (e) {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
      if (pollTimerRef.current) clearInterval(pollTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const startPolling = () => {
    if (pollTimerRef.current) return;
    pollTimerRef.current = setInterval(async () => {
      try {
        const { data } = await api.get(`/chat/messages/${id}`);
        setMessages(data);
        const stillPending = data.some((m) => m.status === "pending");
        if (!stillPending && pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setSending(false);
        }
      } catch {
        /* keep polling */
      }
    }, 1500);
  };

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, sending]);

  const currentStage = project?.current_stage ?? 1;

  const setStage = async (stageId) => {
    try {
      const { data } = await api.patch(`/projects/${id}`, { current_stage: stageId });
      setProject(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const onSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || sending) return;
    setSending(true);
    setError("");
    // Optimistic user message (will be replaced on first poll)
    const optimistic = {
      id: `tmp-user-${Date.now()}`,
      project_id: id,
      role: "user",
      content: text,
      stage: currentStage,
      status: "complete",
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, optimistic]);
    setInput("");
    try {
      // Backend responds immediately after persisting user + pending assistant msgs
      await api.post(`/chat/send`, {
        project_id: id,
        message: text,
        stage: currentStage,
      });
      // Fetch the real messages (replaces optimistic + adds pending assistant)
      const { data } = await api.get(`/chat/messages/${id}`);
      setMessages(data);
      // Poll until the pending assistant message becomes complete/error
      startPolling();
      // Safety timeout: stop polling after 2 minutes max
      setTimeout(() => {
        if (pollTimerRef.current) {
          clearInterval(pollTimerRef.current);
          pollTimerRef.current = null;
          setSending(false);
        }
      }, 120000);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
      setMessages((prev) => prev.filter((m) => m.id !== optimistic.id));
      setSending(false);
    }
  };

  const onClearChat = async () => {
    if (!window.confirm("¿Borrar toda la conversación de este proyecto?")) return;
    try {
      await api.delete(`/chat/messages/${id}`);
      setMessages([]);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const downloadExport = async (format) => {
    try {
      const res = await api.get(`/projects/${id}/export/${format}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      const ext = format === "pdf" ? "pdf" : "md";
      link.download = `inaf_${(project?.title || "proyecto").replace(/\s+/g, "_")}.${ext}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const openMeta = () => {
    setMetaForm({
      title: project.title || "",
      topic: project.topic || "",
      career: project.career || "",
      work_type: project.work_type || "",
      context: project.context || "",
    });
    setShowMeta(true);
  };

  const saveMeta = async () => {
    try {
      const { data } = await api.patch(`/projects/${id}`, metaForm);
      setProject(data);
      setShowMeta(false);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-bone" data-testid="project-loading">
        <div className="flex items-center gap-3 text-neutral-600">
          <Loader2 className="animate-spin" size={18} /> Cargando proyecto…
        </div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-bone gap-3">
        <p className="text-neutral-600">Proyecto no disponible.</p>
        <Link to="/dashboard" className="text-accent-dark underline">Volver al panel</Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone flex flex-col" data-testid="project-editor">
      {/* Top bar */}
      <header className="border-b border-border bg-white sticky top-0 z-20">
        <div className="max-w-screen-2xl mx-auto px-5 md:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4 min-w-0">
            <button
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-1.5 text-sm text-neutral-600 hover:text-primary-800"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft size={16} strokeWidth={1.6} /> Proyectos
            </button>
            <div className="border-l border-border h-8" />
            <div className="min-w-0">
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-accent-dark">
                Etapa {currentStage} · {STAGES[currentStage - 1].name}
              </div>
              <h1 className="font-serif text-xl text-primary-800 truncate leading-tight">
                {project.title}
              </h1>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={openMeta}
              className="hidden md:inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-border rounded-sm hover:bg-neutral-100"
              data-testid="edit-meta-button"
            >
              <Settings2 size={14} strokeWidth={1.6} /> Metadatos
            </button>
            <button
              onClick={() => downloadExport("markdown")}
              className="hidden sm:inline-flex items-center gap-1.5 text-sm px-3 py-2 border border-border rounded-sm hover:bg-neutral-100"
              data-testid="export-md-button"
            >
              <Download size={14} strokeWidth={1.6} /> .md
            </button>
            <button
              onClick={() => downloadExport("pdf")}
              className="inline-flex items-center gap-1.5 text-sm px-3 py-2 bg-primary-800 text-bone rounded-sm hover:bg-primary-700"
              data-testid="export-pdf-button"
            >
              <Download size={14} strokeWidth={1.6} /> PDF
            </button>
          </div>
        </div>
      </header>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-12 max-w-screen-2xl w-full mx-auto">
        {/* Left: Stages */}
        <aside className="lg:col-span-3 border-r border-border bg-primary-800 text-bone min-h-[60vh]">
          <StagesSidebar
            stages={STAGES}
            current={currentStage}
            onSelect={setStage}
          />
        </aside>

        {/* Center: Chat */}
        <main className="lg:col-span-6 flex flex-col min-h-[70vh]">
          <div className="border-b border-border px-6 md:px-8 py-4 bg-white flex items-center justify-between">
            <div>
              <div className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-600">
                Conversación con la asesora
              </div>
              <div className="font-serif text-lg text-primary-800 leading-tight">
                {STAGES[currentStage - 1].name}
              </div>
            </div>
            {messages.length > 0 && (
              <button
                onClick={onClearChat}
                className="text-xs text-neutral-600 hover:text-destructive inline-flex items-center gap-1"
                data-testid="clear-chat-button"
              >
                <Trash2 size={12} /> Limpiar
              </button>
            )}
          </div>

          <div
            className="flex-1 overflow-y-auto px-6 md:px-10 py-8 space-y-8 scroll-inaf"
            data-testid="chat-messages"
          >
            {messages.length === 0 && (
              <WelcomeMessage stage={STAGES[currentStage - 1]} project={project} />
            )}

            {messages.map((m) => (
              <MessageBubble key={m.id} message={m} />
            ))}

            {sending && !messages.some((m) => m.status === "pending") && <TypingBubble />}
            <div ref={chatEndRef} />
          </div>

          {error && (
            <div className="mx-6 md:mx-10 mb-2 px-4 py-2 border border-destructive/30 bg-destructive/5 text-destructive text-sm rounded-sm">
              {error}
            </div>
          )}

          <form onSubmit={onSend} className="border-t border-border p-4 md:p-6 bg-white" data-testid="chat-form">
            <div className="border border-border rounded-sm bg-white focus-within:ring-1 focus-within:ring-accent focus-within:border-accent transition">
              <textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    onSend();
                  }
                }}
                rows={3}
                placeholder={`Escribe aquí tu consulta sobre ${STAGES[currentStage - 1].name.toLowerCase()}...`}
                className="w-full px-4 py-3 resize-none focus:outline-none bg-transparent font-sans text-[0.95rem] leading-relaxed"
                data-testid="chat-input"
                disabled={sending}
              />
              <div className="flex items-center justify-between px-3 pb-2">
                <div className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-600">
                  Enter para enviar · Shift+Enter salto de línea
                </div>
                <button
                  type="submit"
                  disabled={sending || !input.trim()}
                  className="inline-flex items-center gap-1.5 bg-primary-800 text-bone px-4 py-1.5 rounded-sm hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed font-sans text-sm"
                  data-testid="send-message-button"
                >
                  {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                  {sending ? "Enviando…" : "Enviar"}
                </button>
              </div>
            </div>
          </form>
        </main>

        {/* Right: Templates */}
        <aside className="lg:col-span-3 border-l border-border bg-white min-h-[60vh]">
          <TemplatesPanel
            templates={templates}
            activeId={showTemplateId}
            onOpen={setShowTemplateId}
            onInsert={(content) => {
              setInput((v) => (v ? v + "\n\n" : "") + content);
              setShowTemplateId(null);
            }}
            project={project}
          />
        </aside>
      </div>

      {showMeta && metaForm && (
        <div className="fixed inset-0 z-50 bg-primary-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowMeta(false)}>
          <div className="bg-bone border border-border max-w-xl w-full p-8 rounded-sm fade-slide-up" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-xs tracking-[0.28em] uppercase text-accent-dark mb-2">INAF</div>
            <h3 className="font-serif text-2xl text-primary-800 mb-5">Editar metadatos</h3>
            <div className="space-y-4">
              <FormField label="Título">
                <input value={metaForm.title} onChange={(e) => setMetaForm({ ...metaForm, title: e.target.value })} className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent" />
              </FormField>
              <FormField label="Tema preliminar">
                <textarea rows={2} value={metaForm.topic} onChange={(e) => setMetaForm({ ...metaForm, topic: e.target.value })} className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent" />
              </FormField>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField label="Carrera">
                  <input value={metaForm.career} onChange={(e) => setMetaForm({ ...metaForm, career: e.target.value })} className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent" />
                </FormField>
                <FormField label="Tipo de trabajo">
                  <input value={metaForm.work_type} onChange={(e) => setMetaForm({ ...metaForm, work_type: e.target.value })} className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent" />
                </FormField>
              </div>
              <FormField label="Contexto">
                <input value={metaForm.context} onChange={(e) => setMetaForm({ ...metaForm, context: e.target.value })} className="w-full px-4 py-2.5 border border-border rounded-sm bg-white focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent" />
              </FormField>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowMeta(false)} className="px-5 py-2.5 border border-border rounded-sm hover:bg-neutral-100">Cancelar</button>
                <button type="button" onClick={saveMeta} className="px-6 py-2.5 bg-primary-800 text-bone rounded-sm hover:bg-primary-700 font-medium" data-testid="save-meta-button">Guardar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showTemplateId && (
        <TemplateViewer
          templateId={showTemplateId}
          onClose={() => setShowTemplateId(null)}
          onInsert={(content) => {
            setInput((v) => (v ? v + "\n\n" : "") + content);
            setShowTemplateId(null);
          }}
        />
      )}
    </div>
  );
}

// ------- Sub components -------

function StagesSidebar({ stages, current, onSelect }) {
  return (
    <div className="p-6 h-full">
      <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-accent mb-4">
        Proceso investigativo
      </div>
      <ol className="relative" aria-label="Etapas del proceso de investigación">
        {/* vertical line */}
        <span className="absolute left-[15px] top-0 bottom-0 w-px bg-primary-700" aria-hidden />

        {stages.map((s) => {
          const done = s.id < current;
          const active = s.id === current;
          return (
            <li key={s.id} className="relative pl-10 pb-5 last:pb-0">
              <button
                onClick={() => onSelect(s.id)}
                className="group text-left w-full"
                data-testid={`stage-button-${s.id}`}
              >
                <span
                  className={`absolute left-0 top-0.5 w-8 h-8 rounded-full border-2 flex items-center justify-center font-mono text-xs font-bold transition-all
                  ${active ? "bg-accent border-accent text-white" : done ? "bg-primary-700 border-accent text-accent" : "bg-primary-800 border-primary-700 text-neutral-400 group-hover:border-accent/60"}`}
                >
                  {done ? <Check size={14} strokeWidth={2.5} /> : s.id}
                </span>
                <div className={`font-sans text-sm ${active ? "text-bone" : "text-neutral-400 group-hover:text-bone"}`}>
                  {s.name}
                </div>
                <div className={`text-[11px] mt-0.5 ${active ? "text-accent" : "text-neutral-400/70"}`}>
                  {s.description}
                </div>
              </button>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function MessageBubble({ message }) {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === "user";
  const isPending = message.status === "pending";
  const isError = message.status === "error";

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {}
  };

  // Render pending assistant message as a typing bubble
  if (isPending) {
    return (
      <div className="flex gap-3 fade-slide-up" data-testid={`message-${message.id}`}>
        <div className="shrink-0 w-8 h-8 border-2 border-accent rotate-45 flex items-center justify-center mt-1" aria-hidden>
          <div className="w-2 h-2 bg-accent -rotate-45" />
        </div>
        <div>
          <div className="flex items-baseline gap-2 mb-1">
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-600">Asesora IA</span>
            <span className="font-mono text-[10px] text-accent-dark">· redactando respuesta…</span>
          </div>
          <div className="bg-white border border-border rounded-sm px-5 py-4 shadow-sm">
            <span className="typing-dot" />
            <span className="typing-dot" />
            <span className="typing-dot" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex gap-3 fade-slide-up ${isUser ? "justify-end" : "justify-start"}`} data-testid={`message-${message.id}`}>
      {!isUser && (
        <div className="shrink-0 w-8 h-8 border-2 border-accent rotate-45 flex items-center justify-center mt-1" aria-hidden>
          <div className="w-2 h-2 bg-accent -rotate-45" />
        </div>
      )}
      <div className={`max-w-[85%] ${isUser ? "order-1" : ""}`}>
        <div className={`flex items-baseline gap-2 mb-1 ${isUser ? "justify-end" : ""}`}>
          <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-neutral-600">
            {isUser ? "Tú" : "Asesora IA"}
          </span>
          {message.stage && (
            <span className="font-mono text-[10px] text-accent-dark">· Etapa {message.stage}</span>
          )}
        </div>
        {isUser ? (
          <div className="bg-primary-800 text-bone px-4 py-3 rounded-sm leading-relaxed whitespace-pre-wrap break-words">
            {message.content}
          </div>
        ) : (
          <div className={`group bg-white border rounded-sm px-5 py-4 shadow-sm relative ${isError ? "border-destructive/40 bg-destructive/5" : "border-border"}`}>
            <div
              className="prose-inaf"
              dangerouslySetInnerHTML={{ __html: renderMarkdown(message.content) }}
            />
            {!isError && (
              <button
                onClick={copy}
                className="absolute top-2 right-2 text-neutral-400 hover:text-primary-800 opacity-0 group-hover:opacity-100 transition"
                title="Copiar"
              >
                {copied ? <Check size={13} /> : <Copy size={13} />}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex gap-3 fade-slide-up" data-testid="typing-indicator">
      <div className="shrink-0 w-8 h-8 border-2 border-accent rotate-45 flex items-center justify-center mt-1">
        <div className="w-2 h-2 bg-accent -rotate-45" />
      </div>
      <div className="bg-white border border-border rounded-sm px-5 py-4 shadow-sm">
        <span className="typing-dot" />
        <span className="typing-dot" />
        <span className="typing-dot" />
      </div>
    </div>
  );
}

function WelcomeMessage({ stage, project }) {
  return (
    <div className="max-w-2xl mx-auto text-center py-6 fade-slide-up" data-testid="welcome-message">
      <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-accent-dark mb-3">
        Bienvenida · INAF
      </div>
      <h2 className="font-serif text-3xl text-primary-800 leading-tight mb-3">
        Estás en la Etapa {stage.id} — <em className="not-italic">{stage.name}</em>
      </h2>
      <p className="text-neutral-800 leading-relaxed mb-5">
        Soy tu asistente de apoyo metodológico para investigación deportiva del Instituto
        Nacional del Fútbol. Puedo ayudarte a construir tu trabajo desde la idea inicial
        hasta el informe final.
      </p>
      <p className="text-sm text-neutral-800 max-w-lg mx-auto">
        Empecemos: describe brevemente tu idea o fenómeno de interés, y te haré preguntas
        para delimitar tu investigación.
      </p>
      <div className="mt-6 text-xs text-primary-800 font-mono tracking-wide">
        Proyecto: {project.title} {project.career ? `· ${project.career}` : ""}
      </div>
    </div>
  );
}

function TemplatesPanel({ templates, onOpen, onInsert, project }) {
  const notes = project?.stage_notes || {};
  return (
    <div className="p-6 h-full overflow-y-auto scroll-inaf">
      <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-accent-dark mb-4">
        Plantillas rápidas
      </div>
      <div className="space-y-2 mb-8">
        {templates.map((t) => (
          <button
            key={t.id}
            onClick={() => onOpen(t.id)}
            className="w-full text-left border border-border rounded-sm p-3 hover:bg-neutral-100 transition group"
            data-testid={`template-${t.id}`}
          >
            <div className="flex items-start gap-2">
              <BookOpen size={14} strokeWidth={1.6} className="mt-0.5 text-accent-dark" />
              <div>
                <div className="font-serif text-base text-primary-800 leading-tight">
                  {t.title}
                </div>
                <div className="text-xs text-neutral-600 mt-0.5 leading-snug">
                  {t.description}
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="border-t border-border pt-5 mt-5">
        <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-accent-dark mb-3">
          Recordatorios
        </div>
        <ul className="space-y-2 text-xs text-neutral-800 leading-relaxed">
          <li className="flex gap-2"><span className="text-accent-dark">·</span>Nunca fabriques datos ni citas.</li>
          <li className="flex gap-2"><span className="text-accent-dark">·</span>Cita siempre en norma APA 7.</li>
          <li className="flex gap-2"><span className="text-accent-dark">·</span>Verifica coherencia entre problema, pregunta y objetivos.</li>
          <li className="flex gap-2"><span className="text-accent-dark">·</span>Prioriza estudios viables y aplicables.</li>
        </ul>
      </div>
    </div>
  );
}

function TemplateViewer({ templateId, onClose, onInsert }) {
  const [template, setTemplate] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/templates/${templateId}`);
        setTemplate(data);
      } catch {}
    })();
  }, [templateId]);

  return (
    <div className="fixed inset-0 z-50 bg-primary-900/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-bone border border-border max-w-3xl w-full max-h-[85vh] flex flex-col rounded-sm fade-slide-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="template-viewer"
      >
        <div className="p-6 border-b border-border flex items-center justify-between">
          <div>
            <div className="font-mono text-xs tracking-[0.28em] uppercase text-accent-dark">Plantilla</div>
            <h3 className="font-serif text-2xl text-primary-800">{template?.title || "Cargando…"}</h3>
          </div>
          <button onClick={onClose} className="text-neutral-600 hover:text-primary-800">Cerrar</button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 scroll-inaf">
          {template ? (
            <div className="prose-inaf" dangerouslySetInnerHTML={{ __html: renderMarkdown(template.content) }} />
          ) : (
            <p className="text-neutral-600">Cargando plantilla…</p>
          )}
        </div>
        {template && (
          <div className="p-5 border-t border-border flex gap-3 justify-end">
            <button
              onClick={() => navigator.clipboard.writeText(template.content)}
              className="px-5 py-2 border border-border rounded-sm hover:bg-neutral-100 text-sm"
              data-testid="template-copy-button"
            >
              <Copy size={13} className="inline mr-1.5" /> Copiar
            </button>
            <button
              onClick={() => onInsert(template.content)}
              className="px-5 py-2 bg-primary-800 text-bone rounded-sm hover:bg-primary-700 text-sm font-medium"
              data-testid="template-insert-button"
            >
              Insertar en el chat
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <label className="block">
      <span className="block font-mono text-[11px] tracking-[0.18em] uppercase text-neutral-600 mb-1.5">
        {label}
      </span>
      {children}
    </label>
  );
}
